/*
 * Arpe & Company Custom JavaScript
 * Author: ARPE & COMPANY ApS
 * Version: 1.0.0
 * Description: Custom functionality layer on top of Stretch theme for VIA Copenhagen
 * 
 * MODULES:
 * 1. AC Core System
 * 2. Custom Components
 * 3. Utilities
 * 4. Debug Tools
 */

// ================================
// 1. AC CORE SYSTEM
// ================================

const AC = {
  version: '1.0.0',
  debug: false,
  
  // Initialize all AC modules
  init() {
    console.log(`🚀 AC Custom Layer v${this.version} initializing...`);
    
  // Initialize modules
  this.utils.init();
    
    // Set up global event listeners
    this.setupGlobalEvents();
    
    console.log('✅ AC Custom Layer initialized successfully');
  },
  
  // Global event setup
  setupGlobalEvents() {
    // Listen for theme changes (no header adaptive reinit)
    document.addEventListener('shopify:section:load', () => {
      // Placeholder for future section load hooks
    });
    // Listen for resize events (no header adaptive handling)
    window.addEventListener('resize', this.utils.debounce(() => {
      // Placeholder for future resize hooks
    }, 250));
  },
  
  // Debug mode toggle
  toggleDebug() {
    this.debug = !this.debug;
    document.body.classList.toggle('ac-debug', this.debug);
    console.log(`🔍 AC Debug mode: ${this.debug ? 'ON' : 'OFF'}`);
  }
};

// 2. HEADER ADAPTIVE SYSTEM - fully removed

// ================================
// 3. CUSTOM COMPONENTS
// ================================

AC.components = {
  // Enhanced card interactions
  initCards() {
    const cards = document.querySelectorAll('.ac-card');
    cards.forEach(card => {
      card.addEventListener('mouseenter', this.handleCardHover);
      card.addEventListener('mouseleave', this.handleCardLeave);
    });
  },
  
  handleCardHover(e) {
    e.currentTarget.style.transform = 'translateY(-4px)';
  },
  
  handleCardLeave(e) {
    e.currentTarget.style.transform = '';
  },
  
  // Custom button enhancements
  initButtons() {
    const buttons = document.querySelectorAll('.ac-btn');
    buttons.forEach(button => {
      button.addEventListener('click', this.handleButtonClick);
    });
  },
  
  handleButtonClick(e) {
    // Add ripple effect or analytics tracking here
    console.log('AC Button clicked:', e.currentTarget);
  }
};

// ================================
// 4. UTILITIES
// ================================

AC.utils = {
  init() {
    // Initialize utility functions
    console.log('🔧 AC Utilities initialized');
  },
  
  // Throttle function for performance
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  },
  
  // Debounce function for performance  
  debounce(func, delay) {
    let timeoutId;
    return function() {
      const args = arguments;
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(context, args), delay);
    }
  },
  
  // Check if element is in viewport
  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },
  
  // Get device type
  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  },
  
  // Local storage helpers
  storage: {
    set(key, value) {
      try {
        localStorage.setItem(`ac_${key}`, JSON.stringify(value));
      } catch (e) {
        console.warn('AC Storage: Could not save to localStorage', e);
      }
    },
    
    get(key) {
      try {
        const item = localStorage.getItem(`ac_${key}`);
        return item ? JSON.parse(item) : null;
      } catch (e) {
        console.warn('AC Storage: Could not read from localStorage', e);
        return null;
      }
    },
    
    remove(key) {
      try {
        localStorage.removeItem(`ac_${key}`);
      } catch (e) {
        console.warn('AC Storage: Could not remove from localStorage', e);
      }
    }
  }
};

// ================================
// 5. DEBUG TOOLS
// ================================

AC.debug = {
  // Performance monitoring
  performance: {
    marks: new Map(),
    
    mark(name) {
      this.marks.set(name, performance.now());
    },
    
    measure(name, startMark) {
      const start = this.marks.get(startMark);
      if (start) {
        const duration = performance.now() - start;
        console.log(`⏱️ AC Performance [${name}]: ${duration.toFixed(2)}ms`);
      }
    }
  }
};

// ================================
// INITIALIZATION
// ================================

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    AC.init();
  });
} else {
  AC.init();
}

// Expose AC globally for console access
window.AC = AC;

// Debug helper - can be called from console
window.toggleACDebug = () => AC.toggleDebug();

// Header testing helpers removed
